import { catchError, distinctUntilChanged, filter, firstValueFrom, forkJoin, lastValueFrom, map, NotFoundError, Observable, of } from "rxjs";
import { TealHealthEncounter, TealHealthEncounterHeader } from "../models/encounter";
import { ApiService, Diaries, Diary, DiaryInsert, DiaryUpdate, DraftEmail, DraftEmailInsert, Emails, EncounterDraftEmail, Fax, FaxWrite, ErrorMessage, HarloweSupport, Note, NoteAddendum, NoteAddendumInsert, NoteInsert, NoteUpdate, ReceivedEmail, SentEmail, SignalingService, UserService, UserState, User } from "@harlowe/core";
import { inject, Injectable } from "@angular/core";
import { Store } from "@ngxs/store";
import { MessageService } from "primeng/api";
import { environment } from "../../environments/environment";
import { plainToClass, plainToInstance } from "class-transformer";
import { formatDate } from "@angular/common";
import { Encounters } from "../state/encounters/encounter-actions";
import { HubConnection } from "@microsoft/signalr";


export abstract class EncounterServiceBase {
    abstract generateEncounter(encounterId: number): Observable<TealHealthEncounter>
    abstract getEncounter(encounterId: number): Observable<TealHealthEncounter | undefined>;
    abstract getEncounters(encounterId: number[]): Promise<TealHealthEncounterHeader[]>;
    abstract getEncounterDiaries(encounterId: number): Observable<Diary[]>;
    abstract getEncounterNotes(encounterId: number): Observable<Note[]>
    abstract getEncounterSentEmails(encounterId: number): Observable<SentEmail[]>;
    abstract getReceivedNoCategoryEmails(): Observable<ReceivedEmail[]>;
    abstract getEncounterReceivedEmails(encounterId: number): Observable<ReceivedEmail[]>;
    abstract getEncounterUnreadReceivedEmail(emailId: number): Observable<ReceivedEmail>;
    abstract addNewDiary(encounterId: number, diary: DiaryInsert): Promise<Diary>;
    abstract addNewNote(encounterId: number, note: NoteInsert): Promise<Note>;
    abstract updateDiary(encounterId: number, diaryId: number, diary: DiaryUpdate): Promise<Diary>;
    abstract assignDiary(encounterId: number, diaryId: number, userId: number, dueDate?: Date): Promise<Diary>;
    abstract closeDiary(encounterId: number, diaryId: number): Promise<Diary>;
    abstract updateNote(encounterId: number, noteId: number, note: NoteUpdate): Promise<Note>;
    abstract deleteNote(encounterId: number, noteId: number): void;
    abstract addNoteAddendum(encounterId: number, noteId: number, addendum: NoteAddendumInsert): Promise<NoteAddendum>;
    abstract searchForEncounters(filter: EncounterFilter): Promise<TealHealthEncounterHeader[]>
    abstract addEncounterDraftEmail(encounterId: number, email: DraftEmailInsert): Promise<DraftEmail | undefined>;
    abstract sendNewEmail(encounterId: number, email: DraftEmailInsert): Promise<SentEmail | undefined>;
    abstract sendEncounterDraftEmail(draft: EncounterDraftEmail): Promise<SentEmail | undefined>
    abstract getEncounterDraftEmail(encounterId: number, emailId: number): Promise<EncounterDraftEmail>
    abstract getEncounterDraftEmails(encounterId: number): Promise<EncounterDraftEmail[]>;
    abstract sendEncounterFax(encounterId: number, fax: FaxWrite): Promise<Fax>;
    abstract getEncounterFaxes(encounterId: number): Observable<Fax[]>
    abstract addAttachments(encounterId: number, files: FileUpload[]): Promise<HarloweSupport.File[]>;
    abstract deleteAttachment(encounterId: number, fileId: number): Promise<HarloweSupport.File>;
    abstract downloadEncounterFile(encounterId: number, fileId: string, contentType: string): Promise<Blob>;
}

@Injectable({ providedIn: 'root' })
export class EncounterService extends EncounterServiceBase {
    currentUser$ = this._store.select(UserState.currentUser);
    private _encounterHub: HubConnection;

    private _api: ApiService = inject(ApiService);
    private _user: UserService = inject(UserService);
    private _signaling: SignalingService = inject(SignalingService)

    constructor(private _store: Store, private _messages: MessageService) {
        super();

        this._encounterHub = this._signaling.generateHub(environment.harlowe.tealhealth.baseUri + environment.harlowe.tealhealth.signaling.endpoints.encounters);

        this.currentUser$
            .pipe(
                filter((cu): cu is User => cu != null && cu.id != null),
                distinctUntilChanged((a, b) => a.id === b.id)
            )
            .subscribe(cu => {
                if (cu?.id != null) {
                    this._signaling.startHub(this._encounterHub);
                }
                else {
                    this._encounterHub.stop().catch(err => console.error('Hub stop failed', err));
                }
            }
            );

        //Handle inbound hub messages
        //When a referral is updated
        this._encounterHub.on("DiaryAdded", (encounterId: number, diary: Diary) => _store.dispatch(new Encounters.DiaryAdded({ encounterId: encounterId, diary: diary })));
        this._encounterHub.on("DiaryUpdated", (encounterId: number, diary: Diary) => this.diaryUpdated(encounterId, diary));
        this._encounterHub.on("NoteAdded", (encounterId: number, note: Note) => _store.dispatch(new Encounters.NoteAdded({ encounterId: encounterId, note: note })));
        this._encounterHub.on("NoteUpdated", (encounterId: number, note: Note) => _store.dispatch(new Encounters.NoteUpdated({ encounterId: encounterId, note: note })));
        this._encounterHub.on("NoteAddendumAdded", (encounterId: number, noteId: number, addendum: NoteAddendum) => _store.dispatch(new Encounters.NoteAddendumAdded({ encounterId, noteId, addendum })));
        this._encounterHub.on("NoteDeleted", (encounterId: number, noteId: number) => _store.dispatch(new Encounters.NoteDeleted({ encounterId: encounterId, noteId: noteId })));

        //When an email is received
        this._encounterHub.on("EncounterEmailSent", (encounterId: number, email: SentEmail) => _store.dispatch(new Encounters.EncounterServiceEncounterEmailSent({ encounterId: encounterId, email: plainToInstance(SentEmail, email) })));
        this._encounterHub.on("ReceivedEmailLinked", (encounterId: number, email: ReceivedEmail) => _store.dispatch(new Encounters.EncounterServiceEncounterEmailReceived({ encounterId: encounterId, email: plainToInstance(ReceivedEmail, email) })));
        //When an attachment is added
        this._encounterHub.on("AttachmentAdded", (encounterId: number, file: HarloweSupport.File) => _store.dispatch(new Encounters.EncounterServiceAttachmentAdded({ encounterId: encounterId, file: file })));

        //When an attachment is deleted
        this._encounterHub.on("AttachmentDeleted", (encounterId: number, file: HarloweSupport.File) => _store.dispatch(new Encounters.EncounterServiceAttachmentDeleted({ encounterId: encounterId, file: file })));

    }

    override generateEncounter(encounterId: number): Observable<TealHealthEncounter> {
        return this._api.makeAPIPutCall<TealHealthEncounter>(environment.harlowe.tealhealth.baseUri, `/encounters/${encounterId.toString()}`, {})
            .pipe
            (
                map
                    (
                        e => { return plainToClass(TealHealthEncounter, e); }
                    )
            )

    }


    override getEncounter(encounterId: number): Observable<TealHealthEncounter | undefined> {
        return this._api.makeAPIGetCall<TealHealthEncounter>(environment.harlowe.tealhealth.baseUri, `/encounters/${encounterId.toString()}`, {})
            .pipe
            (
                map(e => {
                    var encounter = plainToClass(TealHealthEncounter, e);
                    return encounter;
                }
                ),
                catchError((error: NotFoundError) => { return of(undefined) })
            )
    }

    override getEncounters(encounterId: number[]): Promise<TealHealthEncounterHeader[]> {
        return lastValueFrom
            (
                this._api.makeAPIPostCall<TealHealthEncounterHeader[]>(environment.harlowe.tealhealth.baseUri, "/encounters/list",
                    {
                        encounterIds: encounterId
                    }, {}
                ).pipe
                    (
                        catchError((error: NotFoundError) => { return of([] as TealHealthEncounterHeader[]) })
                    )
            )
    }


    override getEncounterDiaries(encounterId: number): Observable<Diary[]> {
        return this._api.makeAPIGetCall<Diary[]>(environment.harlowe.tealhealth.baseUri, "/encounters/" + encounterId.toString() + "/diaries", {})
            .pipe(catchError((error: NotFoundError) => { return of([] as Diary[]) }))
    }

    override getEncounterNotes(encounterId: number): Observable<Note[]> {
        return this._api.makeAPIGetCall<Note[]>(environment.harlowe.tealhealth.baseUri, "/encounters/" + encounterId.toString() + "/notes", {})
            .pipe
            (
                map(notes => notes.map(n => plainToInstance(Note, n))),

                catchError((error: NotFoundError) => { return of([] as Note[]) })
            )
    }


    override getEncounterSentEmails(encounterId: number): Observable<SentEmail[]> {
        return this._api.makeAPIGetCall<SentEmail[]>(environment.harlowe.tealhealth.baseUri, "/encounters/" + encounterId.toString() + "/emails/sent", {})
            .pipe
            (
                map(emails => emails.map(e => plainToInstance(SentEmail, e))),

                catchError((error: NotFoundError) => { return of([] as SentEmail[]) })
            )
    }

    override getEncounterReceivedEmails(encounterId: number): Observable<ReceivedEmail[]> {
        return this._api.makeAPIGetCall<ReceivedEmail[]>(environment.harlowe.tealhealth.baseUri, "/encounters/" + encounterId.toString() + "/emails/received", {})
            .pipe
            (
                map(emails => emails.map(e => plainToInstance(ReceivedEmail, e))),

                catchError((error: NotFoundError) => { return of([] as ReceivedEmail[]) })
            )
    }

    override addNewDiary(encounterId: number, diary: DiaryInsert): Promise<Diary> {
        return lastValueFrom
            (
                this._api.makeAPIPutCall<Diary>(environment.harlowe.tealhealth.baseUri, "/encounters/" + encounterId.toString() + "/diary", diary)
                    .pipe
                    (
                        map
                            (
                                d => {
                                    this._store.dispatch(new Diaries.EncounterServiceDiaryCreated({ diaryId: d.id, diary: d }));
                                    return d;
                                }
                            )
                    )
            )
    }

    override addNewNote(encounterId: number, note: NoteInsert): Promise<Note> {
        return lastValueFrom
            (
                this._api.makeAPIPutCall<Note>(environment.harlowe.tealhealth.baseUri, "/encounters/" + encounterId.toString() + "/note", note)
            )
    }

    override updateDiary(encounterId: number, diaryId: number, diary: DiaryUpdate): Promise<Diary> {
        return lastValueFrom
            (
                this._api.makeAPIPatchCall<Diary>(environment.harlowe.tealhealth.baseUri, "/encounters/" + encounterId.toString() + "/diary/" + diaryId.toString(), diary)
            )
    }

    override async assignDiary(encounterId: number, diaryId: number, userId: number, dueDate?: Date): Promise<Diary> {
        var du = new DiaryUpdate(diaryId);
        du.assignedToId = userId;
        if (dueDate != null)
            du.dueDate = dueDate;
        var diary = await this.updateDiary(encounterId, diaryId, du);
        this._store.dispatch(new Diaries.EncounterServiceDiaryAssigned({ diaryId: diaryId, userId: userId }));

        return diary;
    }

    override async closeDiary(encounterId: number, diaryId: number): Promise<Diary> {
        var du = new DiaryUpdate(diaryId);
        du.closedDate = new Date();
        var currentUser = this._store.selectSnapshot(UserState.currentUser);
        du.closedById = currentUser?.id;
        var diary = await this.updateDiary(encounterId, diaryId, du)
        this._store.dispatch(new Diaries.EncounterServiceDiaryClosed({ diaryId: diaryId }));
        return diary;
    }

    override updateNote(encounterId: number, noteId: number, note: NoteUpdate): Promise<Note> {
        return lastValueFrom
            (
                this._api.makeAPIPutCall<Note>(environment.harlowe.tealhealth.baseUri, "/encounters/" + encounterId.toString() + "/note/" + noteId.toString(), note)
            )
    }

    override async deleteNote(encounterId: number, noteId: number ): Promise<void> {
        const obs$ = this._api.makeAPIDeleteCall<Note>(environment.harlowe.tealhealth.baseUri, "/encounters/" + encounterId.toString() + "/note/" + noteId.toString())
        await firstValueFrom(obs$);
    }

    override addNoteAddendum(encounterId: number, noteId: number, addendum: NoteAddendumInsert): Promise<NoteAddendum> {
        return lastValueFrom
            (
                this._api.makeAPIPutCall<NoteAddendum>(environment.harlowe.tealhealth.baseUri, "/encounters/" + encounterId.toString() + "/note/" + noteId.toString() + "/addendum", addendum)
            )
    }

    override sendNewEmail(encounterId: number, email: DraftEmailInsert): Promise<SentEmail | undefined> {
        return lastValueFrom(
            this._api.makeAPIPostCall<SentEmail>(environment.harlowe.tealhealth.baseUri, "/encounters/" + encounterId.toString() + "/emails/send", email)
                .pipe
                (
                    map
                        (
                            e => {
                                this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Email Sent' });
                                this._store.dispatch(new Encounters.EncounterServiceEncounterEmailSent({ encounterId: encounterId, email: e }));
                                return plainToClass(SentEmail, e);
                            }
                        ),
                    catchError((error: ErrorMessage) => { this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could Not Send Email', detail: ' ' + error.message }); return of(undefined) })
                ));
    }

    override sendEncounterDraftEmail(draft: EncounterDraftEmail): Promise<SentEmail | undefined> {
        return lastValueFrom(
            this._api.makeAPIPostCall<SentEmail>(environment.harlowe.tealhealth.baseUri, "/encounters/" + draft.encounterId.toString() + "/emails/drafts/" + draft.id?.toString() + "/send", {})
                .pipe
                (
                    map
                        (
                            e => {
                                this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Email Sent' });
                                this._store.dispatch(new Encounters.EncounterServiceEncounterEmailSent({ encounterId: draft.encounterId, email: e }));
                                if (draft.id) this._store.dispatch(new Emails.EmailServiceEmailDraftSent({ draftEmailId: draft.id }))
                                return plainToClass(SentEmail, e);
                            }
                        ),
                    catchError((error: ErrorMessage) => { this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could Not Send Email', detail: ' ' + error.message }); return of(undefined) })


                ));
    }

    override getEncounterDraftEmail(encounterId: number, emailId: number): Promise<EncounterDraftEmail> {
        throw new Error("Method not implemented.");
    }

    override getEncounterDraftEmails(encounterId: number): Promise<EncounterDraftEmail[]> {
        return lastValueFrom(this._api.makeAPIGetCall<EncounterDraftEmail[]>(environment.harlowe.tealhealth.baseUri, "/encounters/" + encounterId.toString() + "/emails/drafts", {})
            .pipe
            (
                map(drafts => drafts.map(draft => plainToInstance(EncounterDraftEmail, draft))),
                catchError(() => of([] as EncounterDraftEmail[]))
            ));
    }


    override addEncounterDraftEmail(encounterId: number, email: EncounterDraftEmail): Promise<EncounterDraftEmail | undefined> {
        return lastValueFrom(
            this._api.makeAPIPutCall<EncounterDraftEmail>(environment.harlowe.tealhealth.baseUri, "/encounters/" + encounterId.toString() + "/draft", email)
                .pipe
                (
                    map
                        (
                            e => {
                                this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Draft Saved! You can access it from the email sidebar.' });
                                this._store.dispatch(new Emails.EmailServiceEncounterEmailDraftAdded({ draftEmail: e }));
                                return plainToClass(EncounterDraftEmail, e);
                            }
                        ),
                    catchError((error: ErrorMessage) => { this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could Not Create Draft Email', detail: ' ' + error.message }); return of(undefined) })
                )
        )
    }

    override getReceivedNoCategoryEmails(): Observable<ReceivedEmail[]> {
        throw new Error("Method not implemented.");
    }
    override getEncounterUnreadReceivedEmail(emailId: number): Observable<ReceivedEmail> {
        throw new Error("Method not implemented.");
    }


    diaryUpdated(encounterId: number, diary: Diary) {
        //Update the date fields in the diary object
        if (diary.dueDate)
            diary.dueDate = new Date(diary.dueDate);
        if (diary.closedDate)
            diary.closedDate = new Date(diary.closedDate);
        diary.createdDate = new Date(diary.createdDate);
        this._store.dispatch(new Encounters.DiaryUpdated({ encounterId: encounterId, diary: diary }));
        this._store.dispatch(new Diaries.EncounterServiceDiaryUpdated({ diaryId: diary.id, diary: diary }))
    }

    override searchForEncounters(filter: EncounterFilter): Promise<TealHealthEncounterHeader[]> {
        return lastValueFrom
            (
                this._api.makeAPIGetCall<TealHealthEncounterHeader[]>(environment.harlowe.tealhealth.baseUri, "/encounters/",
                    {
                        ...filter.createdAfter && { createdAfter: formatDate(filter.createdAfter, "MM/dd/yyyy", "en-us") },
                        ...filter.createdBefore && { createdBefore: formatDate(filter.createdBefore, "MM/dd/yyyy", "en-us") },
                        ...filter.processedAfter && { processedAfter: formatDate(filter.processedAfter, "MM/dd/yyyy", "en-us") },
                        ...filter.processedBefore && { processedBefore: formatDate(filter.processedBefore, "MM/dd/yyyy", "en-us") },
                        ...filter.statuses && { statuses: filter.statuses.map(s => s.toString()) },
                        ...filter.ownedByIds && { ownedBy: filter.ownedByIds.map(o => o.toString()) },
                        // ... filter.includeUnassigned && {includeUnassigned: filter.includeUnassigned.toString()}
                    }).pipe
                    (
                        catchError((error: NotFoundError) => { this._messages.add({ key: 'main-alert', severity: 'error', summary: 'No Encounters Found' }); return of([]) })
                    )
            )


    }


    override addAttachments(encounterId: number, files: FileUpload[]): Promise<HarloweSupport.File[]> {
        return lastValueFrom
            (
                forkJoin
                    (
                        files.map
                            (
                                f => {
                                    var d: FormData = new FormData();
                                    d.append('file', f);
                                    if (f.category)
                                        d.append('category', f.category)
                                    if (f.fileType)
                                        d.append('fileType', f.fileType)

                                    return this._api.makeAPIPutCall<HarloweSupport.File>(environment.harlowe.tealhealth.baseUri, "/encounters/" + encounterId.toString() + "/attachments", d, {})
                                }
                            )
                    )
                    .pipe
                    (
                        map
                            (
                                f => {
                                    var newFiles = f.map(ff => plainToInstance(HarloweSupport.File, ff));
                                    this._store.dispatch(new Encounters.EncounterServiceAttachmentsAdded({ encounterId: encounterId, files: newFiles }));
                                    return f;
                                }
                            )
                    )
            )
    }

    override deleteAttachment(encounterId: number, fileId: number): Promise<HarloweSupport.File> {
        return lastValueFrom
            (
                this._api.makeAPIDeleteCall<HarloweSupport.File>(environment.harlowe.tealhealth.baseUri, "/encounters/" + encounterId.toString() + "/attachments/" + fileId.toString(), {})

                    .pipe
                    (
                        map
                            (
                                f => {
                                    this._store.dispatch(new Encounters.EncounterServiceAttachmentDeleted({ encounterId: encounterId, file: f }));
                                    return f;
                                }
                            )
                    )
            )
    }

    override getEncounterFaxes(encounterId: number): Observable<Fax[]> {
        return this._api.makeAPIGetCall<Fax[]>(environment.harlowe.tealhealth.baseUri, "/encounters/" + encounterId.toString() + "/faxes", {})
            .pipe(
                map(faxes => faxes.map(fax => plainToInstance(Fax, fax)))
            );
    }

    override sendEncounterFax(encounterId: number, fax: FaxWrite): Promise<Fax> {
        return lastValueFrom(this._api.makeAPIPostCall<Fax>(environment.harlowe.tealhealth.baseUri, "/encounters/" + encounterId.toString() + "/fax", fax)
            .pipe
            (
                map
                    (
                        ef => {
                            //this._store.dispatch(new Encounters.EncounterService));
                            return ef;
                        }
                    ),
            )
        )
    }

    override downloadEncounterFile(encounterId: number, fileName: string): Promise<Blob> {
        return lastValueFrom(this._api.getFileBlob(environment.harlowe.tealhealth.baseUri, "/encounters/" + encounterId.toString(), { fileName }));
    }

}

export class EncounterFilter {
    statuses: number[] = [];
    ownedByIds: number[] = [];
    createdBefore?: Date;
    createdAfter?: Date;
    processedBefore?: Date;
    processedAfter?: Date;
    includeUnassigned: boolean = false;

}


export class FileUpload extends File {
    category?: string;
    fileType?: string;
}

